import { useCallback, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
// import { type GenericERC721Token, type GenericERC1155Token } from '1stdibs-contracts/typechain';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import {
    getGenericTokenContract,
    getGeneric1155TokenContract,
} from 'dibs-crypto-utils/exports/getContract.ts';

import { type useTransferToken_nftItem$key } from './__generated__/useTransferToken_nftItem.graphql.ts';

// 1stdibs-contracts not compatible with verbatimModuleSyntax
type GenericERC721Token = $TSFixMe;
type GenericERC1155Token = $TSFixMe;

export type TransferTokenStatus = 'inProgress' | 'success' | 'error' | null;

export const useTransferToken = (
    nftItemRef: useTransferToken_nftItem$key | null = null
): [
    handlerTransferToken: (selectedAddress: string | null, editions: number) => Promise<void>,
    transferTokenStatus: TransferTokenStatus,
    reset: () => void,
    transferredEditions: number,
] => {
    const { library, account } = useWeb3Connection();

    const [tokenContract, setTokenContract] = useState<
        GenericERC721Token | GenericERC1155Token | null
    >(null);
    const [transferState, setTransferState] = useState<TransferTokenStatus>(null);
    const [transferredEditions, setTransferredEditions] = useState<number>(1);

    const nftItem = useFragment(
        graphql`
            fragment useTransferToken_nftItem on NftItem {
                tokenId
                contract {
                    address
                }
                balanceForUser(userId: $userId) @include(if: $fetchUser) {
                    balance
                }
                isERC1155
            }
        `,
        nftItemRef
    );

    useEffect(() => {
        (async () => {
            if (library && nftItem && nftItem.contract?.address) {
                try {
                    const contract = nftItem?.isERC1155
                        ? await getGeneric1155TokenContract(library, nftItem.contract.address)
                        : await getGenericTokenContract(library, nftItem.contract.address);
                    if (contract) {
                        setTokenContract(contract);
                    } else {
                        throw new Error('cannot get generic token contract');
                    }
                } catch (e: $TSFixMe) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                    setTransferState('error');
                }
            }
        })();
    }, [library, nftItem]);

    const onTransferClick = useCallback(
        async (selectedAddress: string | null, editions: number): Promise<void> => {
            const signer = library?.getSigner(account ?? '');
            if (editions) {
                setTransferredEditions(editions);
            }
            if (
                signer &&
                account &&
                selectedAddress &&
                tokenContract &&
                library &&
                nftItem?.tokenId &&
                account !== selectedAddress
            ) {
                try {
                    const txn = nftItem?.isERC1155
                        ? await tokenContract
                              .connect(signer)
                              [
                                  'safeTransferFrom(address,address,uint256,uint256,bytes)'
                              ](account, selectedAddress, nftItem.tokenId, editions, '0x')
                        : await tokenContract
                              .connect(signer)
                              [
                                  'safeTransferFrom(address,address,uint256)'
                              ](account, selectedAddress, nftItem.tokenId);

                    setTransferState('inProgress');

                    await txn.wait();

                    setTransferState('success');
                } catch (e: $TSFixMe) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                    setTransferState('error');
                }
            }
        },
        [account, library, nftItem, tokenContract]
    );
    const reset = useCallback(() => setTransferState(null), []);

    return [onTransferClick, transferState, reset, transferredEditions];
};
